/**
 * How to word the warning that a number belongs to somebody we placed.
 *
 * The client's ask was "if a recruiter tries, warn them: this candidate is
 * working at X, don't call them." Two things had to be decided to build it,
 * and both are here rather than in the markup so they can be tested.
 *
 * **The warning appears on the placement, not on the payment.** The server
 * gates on WORKING and YET_TO_JOIN, and this module never reads paymentState
 * to decide whether to speak. The harm exists from the moment they accept,
 * and a placement whose invoice is still unpaid is the *more* dangerous one
 * to disturb: a drop inside the guarantee window can void the fee outright.
 * Gating on "we have been paid" would have stayed silent through exactly that
 * window — which is also when the candidate is most likely to still be
 * sitting on an old call list.
 *
 * **Not yet joined is the sharpest case of all**, which is why it is checked
 * first and is always severe. Somebody poached before they arrive voids the
 * fee with nothing delivered and no guarantee window involved. The gate was
 * WORKING alone to begin with and that was too narrow.
 *
 * **The money sets the words.** A placement we have been paid for and one
 * nobody has billed yet are both reasons not to ring somebody, but they are
 * not the same sentence, and a warning that overstates gets discounted. So
 * paymentState chooses the second line and the guarantee window chooses the
 * colour.
 */
import type { PlacedWarning } from '../types';

export interface PlacedWarningCopy {
  /** The instruction, first and unhedged. */
  headline: string;
  /** Why, in one clause. Null when there is nothing honest to add. */
  detail: string | null;
  /**
   * Whether this is the red version rather than the amber one.
   *
   * Reserved for the cases with money actually at stake right now: a
   * candidate who has not arrived yet, a fee already received, and a
   * guarantee still running. Everything else warns in amber — not because it
   * is fine, but because a warning that shouts on every row is one nobody
   * reads.
   */
  severe: boolean;
}

export function placedWarningCopy(placed: PlacedWarning): PlacedWarningCopy {
  // Named where we can. A placement always has a client, but the name is a
  // separate lookup and a merged-away client can leave it absent -- the
  // sentence has to survive that rather than reading "working at null".
  const named = placed.clientName;

  if (!placed.hasJoined) {
    // The worst case, and the reason the gate is not WORKING alone. Somebody
    // poached before they arrive voids the fee outright: no guarantee window
    // is needed, and nothing has been delivered to show for the work. Always
    // severe, and the money is beside the point -- there is no version of
    // this that is merely amber.
    return {
      headline: named
        ? `Do not call — due to join ${named}`
        : 'Do not call — due to start a job we placed them in',
      detail:
        'We placed them and they have not started yet. A drop before they '
        + 'join voids the fee outright.',
      severe: true,
    };
  }

  const where = named
    ? `working at ${named}`
    : 'working in a job we placed them in';

  const headline = `Do not call — ${where}`;

  if (placed.withinGuarantee) {
    // The sharpest case, and the reason payment is not the gate: a drop here
    // can void the fee we have not necessarily even collected yet.
    return {
      headline,
      detail:
        'We placed them and the replacement guarantee is still running — a '
        + 'drop now can void the fee.',
      severe: true,
    };
  }

  switch (placed.paymentState) {
    case 'received':
      return {
        headline,
        detail: 'We placed them and have been paid for it.',
        severe: true,
      };
    case 'part_paid':
      return {
        headline,
        detail: 'We placed them and part of the fee has been paid.',
        severe: false,
      };
    case 'yet_to_receive':
      return {
        headline,
        detail: 'We placed them and the fee is still outstanding.',
        severe: false,
      };
    case 'cancelled':
      // The claim was withdrawn, so no fee is coming. They are still in the
      // job we put them in, which is the reason not to ring them -- the
      // billing is a separate matter and saying nothing about it is honest.
      return {
        headline,
        detail: 'We placed them, and they are still in the job.',
        severe: false,
      };
    case 'not_invoiced':
    default:
      // Nothing is owed on a placement nobody has billed for, and asserting a
      // fee here would be the overstatement PaymentState.NOT_INVOICED exists
      // to avoid.
      return {
        headline,
        detail: 'We placed them, and they are still in the job.',
        severe: false,
      };
  }
}
